import React, { useMemo, useState } from "react";
import { Box, Chip, Typography } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";

import { GridWrapper } from "../styles";
import {
  formatDisplayDate,
  formatDisplayTime,
  getActivityTypeMeta,
  getActivityCategory,
} from "../utils";

const ActivityLogsGrid = ({ rows, loading, onRowClick }) => {
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 100,
  });

  const columns = useMemo(
    () => [
      {
        field: "activity_type",
        headerName: "Category",
        minWidth: 180,
        flex: 0.9,
        sortable: true,
        valueGetter: (_, row) => getActivityCategory(row),
        renderCell: (params) => {
          const meta = getActivityTypeMeta(params.row);

          return (
            <Chip
              size="small"
              label={meta.shortLabel}
              sx={{
                bgcolor: meta.bg,
                color: meta.color,
                fontWeight: 700,
                border: `1px solid ${meta.color}22`,
              }}
            />
          );
        },
      },
      {
        field: "activity_name",
        headerName: "Event Name",
        minWidth: 280,
        flex: 1.4,
        sortable: false,
        valueGetter: (_, row) => getActivityTypeMeta(row).label,
        renderCell: (params) => {
          const meta = getActivityTypeMeta(params.row);

          return (
            <Typography
              variant="body2"
              fontWeight={700}
              sx={{
                whiteSpace: "normal",
                lineHeight: 1.35,
                py: 1,
                color: meta.color,
              }}
            >
              {meta.label || "-"}
            </Typography>
          );
        },
      },
      {
        field: "activity_details",
        headerName: "Details",
        minWidth: 360,
        flex: 2,
        sortable: false,
        renderCell: (params) => (
          <Typography
            variant="body2"
            fontWeight={600}
            sx={{
              whiteSpace: "normal",
              lineHeight: 1.35,
              py: 1,
            }}
          >
            {params.value || "-"}
          </Typography>
        ),
      },
      {
        field: "user_name",
        headerName: "Provider",
        minWidth: 180,
        flex: 1,
        valueGetter: (_, row) => row.user_name || row.user_id || "-",
      },
      {
        field: "user_id",
        headerName: "User ID",
        minWidth: 220,
        flex: 1.2,
      },
      {
        field: "npi",
        headerName: "NPI",
        minWidth: 130,
        flex: 0.7,
      },
      {
        field: "activity_date_only",
        headerName: "Date",
        minWidth: 140,
        flex: 0.8,
        valueGetter: (_, row) => formatDisplayDate(row.activity_date),
      },
      {
        field: "activity_time_only",
        headerName: "Time",
        minWidth: 120,
        flex: 0.7,
        valueGetter: (_, row) => formatDisplayTime(row.activity_date),
      },
    ],
    [],
  );

  const gridRows = useMemo(
    () =>
      rows.map((item, index) => ({
        ...item,
        id: item.id ?? `${item.activity_date}-${index}`,
      })),
    [rows],
  );

  return (
    <GridWrapper>
      <Box sx={{ width: "100%" }}>
        <DataGrid
          rows={gridRows}
          columns={columns}
          loading={loading}
          autoHeight
          disableRowSelectionOnClick
          pagination
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[100, 150, 200]}
          rowHeight={72}
          onRowClick={(params) => onRowClick?.(params.row)}
          slotProps={{
            loadingOverlay: {
              variant: "linear-progress",
              noRowsVariant: "linear-progress",
            },
          }}
          sx={{
            border: 0,
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#f8fafc",
              borderBottom: "1px solid #e2e8f0",
            },
            "& .MuiDataGrid-row": {
              cursor: "pointer",
            },
            "& .MuiDataGrid-cell": {
              borderBottom: "1px solid #f1f5f9",
              alignItems: "center",
            },
          }}
        />
      </Box>
    </GridWrapper>
  );
};

export default ActivityLogsGrid;
